import React, { useState } from 'react';
import { useAuth0, Auth0Provider } from '@auth0/auth0-react';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const Error = ({ error }) => {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const toggleAdvanced = () => setShowAdvanced(!showAdvanced);
    return (React.createElement("main", { id: 'Error' },
        React.createElement("p", null, "Looks like we encountered an error while attempting to log you in."),
        React.createElement("button", null, "Try Again"),
        React.createElement("button", { onClick: toggleAdvanced },
            showAdvanced ? 'Hide ' : 'Show ',
            "Advanced Details"),
        showAdvanced ? React.createElement("p", null, JSON.stringify(error)) : null));
};

const Loading = () => {
    return React.createElement("main", { id: 'Loading' }, "Loading...");
};

const Login = ({ loginWithRedirect, loginWithPopup, }) => {
    if (loginWithRedirect) {
        return (React.createElement("main", { id: 'Login' },
            React.createElement("button", { onClick: () => loginWithRedirect() }, "Login with Auth0")));
    }
    else if (loginWithPopup) {
        return (React.createElement("main", { id: 'Login' },
            React.createElement("button", { onClick: () => loginWithPopup() }, "Login with Auth0")));
    }
    else {
        return React.createElement("div", null, "error loading Login component");
    }
};

/**
 * wrapper component: Auth
 *
 * Summary:
 * Renders UI based on user's current Auth0 Login state
 *
 * Description:
 * Will return contents of application component tree (children prop)
 *    iff the user has successfully authenticated
 *
 * Will return a loading screen if Auth0 provider indicates a loading state
 *
 * Will return an error screen if Auth0 provider indicates an error state
 *
 * Will return a login screen if Auth0 provider indicates no current user
 *
 * @param children: (automatically populated by React)
 *
 * @param CustomLoading: (optional) React Component to be rendered in place of the default loading screen
 *
 * @param CustomError: (optional) React Component to be rendered in place of the default error screen,
 *    takes in "error" prop with message from Auth0
 *
 * @param CustomError: (optional) React Component to be rendered in place of the default error screen,
 *    takes in "loginWithRedirect" prop as Auth0 login callback function
 *
 * @return JSX element based on current auth state
 */
function useAuth() {
    const auth0Obj = useAuth0();
    function getCreds() {
        return __awaiter(this, void 0, void 0, function* () {
            let accessToken;
            try {
                accessToken = yield auth0Obj.getAccessTokenSilently({
                    audience: 'https://mpsapi.cloud.izotope.com',
                    scope: 'openid profile email',
                });
            }
            catch (err) {
                //for localhost
                accessToken = yield auth0Obj.getAccessTokenWithPopup({
                    audience: 'https://mpsapi.cloud.izotope.com',
                    scope: 'openid profile email',
                });
            }
            const claims = yield auth0Obj.getIdTokenClaims();
            return { idTokenClaims: claims, accessToken };
        });
    }
    return Object.assign(Object.assign({}, auth0Obj), { getCreds });
}
const AuthHandler = ({ children, Loading, Error, Login, usePopup = false, loginRequired = true, }) => {
    const { isAuthenticated, isLoading, error, loginWithRedirect, loginWithPopup, getAccessTokenSilently, getAccessTokenWithPopup, getIdTokenClaims, } = useAuth0();
    if (isAuthenticated || !loginRequired) {
        // return a React fragment containing all children of Auth React node
        // (fragment is the "empty" element tag that will not actually add meaningless empty DOM elements)
        return React.createElement(React.Fragment, null, children);
    }
    if (isLoading) {
        // return a default loading screen if we are awaiting response from Auth0
        return React.createElement(Loading, null);
    }
    if (error) {
        // return a default error screen if Auth0 returns an error
        console.error('Error from auth0 hook:', error);
        return React.createElement(Error, { error: error });
    }
    if (usePopup) {
        return React.createElement(Login, { loginWithPopup: loginWithPopup });
    }
    else {
        // return a login screen if no logged in user is present
        return React.createElement(Login, { loginWithRedirect: loginWithRedirect });
    }
};
const Auth = ({ children, AUTH0_CONFIG, CustomLoading, CustomError, CustomLogin, loginRequired = true, }) => {
    return (React.createElement(Auth0Provider, Object.assign({ cacheLocation: AUTH0_CONFIG.cacheLocation || 'localstorage' }, AUTH0_CONFIG),
        React.createElement(AuthHandler, { children: children, Loading: CustomLoading || Loading, Error: CustomError || Error, Login: CustomLogin || Login, loginRequired: loginRequired })));
};

export { Auth, useAuth };
